// @flow
import * as React from 'react'
import tw from 'tailwind-styled-components'
import ModalBlueHeader from 'ui/atoms/Modal/ModalBlueHeader'
import Space from 'ui/atoms/Space'

import ModalFooter from './ModalFooter'
import ModalHeader from './ModalHeader'

type Props = {
  closeButton?: boolean,
  show?: boolean,
  onHide?: () => void,
  dismissOnOutsideClick?: boolean,
  loading?: boolean,
  children?: React.Node,
  size?: 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge',
  withStopPropagation?: boolean,
  blueHeader?: boolean,
  title?: string,
  subtitle?: string,
  primaryAction?: boolean,
  secondaryAction?: boolean,
  primaryActionText?: string,
  secondaryActionText?: string,
  onPrimaryAction?: () => any,
  onSecondaryAction?: () => any,
}

const Background = tw.div`
  bg-black
  bg-opacity-50
  bottom-0
  clearfix
  cursor-default
  fixed
  left-0
  m-0
  overflow-auto
  right-0
  top-0
  z-9999
`

const ModalBody: React.ComponentType<{}> = tw.div`
  bg-white
  ${(props: { $size?: string }) => {
    switch (props.$size) {
      case 'small':
        return 'lg:w-1/3'
      case 'medium':
        return 'lg:w-1/2'
      case 'large':
        return 'lg:w-2/3'
      case 'xlarge':
        return 'lg:w-3/4'
      case 'xxlarge':
        return 'lg:w-4/5'
      default:
        return 'lg:w-1/2'
    }
  }}
  p-4
  rounded
  relative
  shadow
  w-11/12
  md:w-8/12
 `

// TODO: move to molecules. this is no atom anymore.

function Modal({
  children,
  closeButton,
  dismissOnOutsideClick,
  onHide,
  show,
  size,
  withStopPropagation = true,
  blueHeader,
  title,
  subtitle,
  primaryAction,
  secondaryAction,
  primaryActionText = 'Save',
  loading,
  secondaryActionText = 'Cancel',
  onPrimaryAction,
  onSecondaryAction,
}: Props): React.Node {
  const handleOutsideClick = React.useCallback(
    (event) => {
      if (withStopPropagation) {
        event.preventDefault()
      }

      if (dismissOnOutsideClick && onHide) {
        onHide()
      }
    },
    [dismissOnOutsideClick, onHide, withStopPropagation]
  )

  const stopPropagation = React.useCallback(
    (event) => {
      if (!withStopPropagation) return
      event.stopPropagation()
      event.preventDefault()
    },
    [withStopPropagation]
  )

  if (!show) return null

  return (
    <Background onClick={handleOutsideClick}>
      <ModalBody
        $size={size}
        onClick={stopPropagation}
        style={{ margin: '5% auto' }}
      >
        <Space size="large">
          {blueHeader ? (
            <ModalBlueHeader
              closeButton={closeButton}
              onHide={onHide}
              subtitle={subtitle}
              title={title}
            />
          ) : (
            <ModalHeader
              closeButton={closeButton}
              onHide={onHide}
              subtitle={subtitle}
              title={title}
            />
          )}
          <div>{children}</div>
          <ModalFooter
            loading={loading}
            onPrimaryAction={onPrimaryAction}
            onSecondaryAction={onSecondaryAction || onHide}
            primaryAction={primaryAction}
            primaryActionText={primaryActionText}
            secondaryAction={secondaryAction}
            secondaryActionText={secondaryActionText}
          />
        </Space>
      </ModalBody>
    </Background>
  )
}

export default Modal
